import { Card, Divider, Space, theme, Typography } from "antd";

import { ValueFormatter } from "src/utils/formatters";
import React from "react";
import { getChangeIcon, isValidPercent } from "../cardUtils";
import { formatTitle } from "src/utils/strings";
import { CustomTooltip } from "src/components/CustomTooltip";

export interface MetricDisplayProps {
  label?: string;
  value: number | string;
  unit?: string;
  change?: number | string;
  changeType?: "increase" | "decrease" | "neutral" | "";
  percent?: string;
  variant?:
    | "default"
    | "card"
    | "modern"
    | "minimal"
    | "low-profile"
    | "horizontal-compact"
    | "table-cell";
  className?: string;
  style?: React.CSSProperties;
  extra?: React.ReactNode;
  color?: string; // for card backgrounds
  density?: "default" | "compact";
  showDivider?: boolean;
}

export function MetricDisplay({
  label,
  value,
  unit,
  change,
  changeType,
  percent,
  variant = "default",
  className,
  style,
  extra,
  color,
  density = "default",
  showDivider,
}: MetricDisplayProps) {
  const { token } = theme.useToken();
  const formattedValue =
    typeof value === "number"
      ? ValueFormatter.format(value, { unit, precision: 1 })
      : value;

  function getHeaderClass(formattedValue: string) {
    if (!formattedValue || formattedValue === "0") return "vzbl-header-3";

    // Remove non-digit characters for length calculation
    const digitCount = formattedValue.replace(/\D/g, "").length;
    if (digitCount <= 3) return "vzbl-header-1";
    if (digitCount <= 5) return "vzbl-header-2";
    if (digitCount <= 7) return "vzbl-header-3";
    return "vzbl-header-4";
  }

  // --- Variant: Horizontal Compact ---
  if (variant === "horizontal-compact") {
    return (
      <Space
        direction="vertical"
        size={density === "compact" ? 2 : "small"}
        className={`${className} vzbl-card-metric`}
        style={{ width: "100%", ...style }}
      >
        {label && (
          <div style={{ minWidth: 0, width: "100%" }}>
            <CustomTooltip content={formatTitle(label || "")}>
              <Typography.Text
                className="vzbl-text-2 vzbl-clamp-1-line"
                style={{ maxWidth: "100%" }}
              >
                {formatTitle(label)}
              </Typography.Text>
            </CustomTooltip>
          </div>
        )}
        <Space direction="vertical" size={2} style={{ width: "100%" }}>
          <Space>
            <CustomTooltip content={formatTitle(label || "")}>
              <Typography.Text className={"vzbl-header-3 vzbl-clamp-1-line"}>
                {formattedValue}
              </Typography.Text>
            </CustomTooltip>
            {(isValidPercent(change) || changeType) && (
              <Space size={2}>
                {getChangeIcon(changeType)}
                {change !== "0%" && (
                  <Typography.Text className="vzbl-text-3">
                    {typeof change === "number" && change > 0 ? "+" : ""}
                    {change}
                  </Typography.Text>
                )}
              </Space>
            )}
          </Space>
          {unit && (
            <Typography.Text className="vzbl-text-2 vzbl-clamp-1-line">
              {unit}
            </Typography.Text>
          )}
        </Space>
      </Space>
    );
  }

  // --- Variant: Table Cell ---
  if (variant === "table-cell") {
    return (
      <div
        className={className}
        style={{
          textAlign: "right",
          padding: density === "compact" ? "2px 4px" : "6px 12px",
          ...style,
        }}
      >
        <CustomTooltip content={formatTitle(label || "")}>
          <Typography.Text
            className={`${getHeaderClass(formattedValue)} vzbl-clamp-1-line`}
          >
            {formattedValue}
          </Typography.Text>
        </CustomTooltip>
        {unit && (
          <Typography.Text className="vzbl-text-2">{unit}</Typography.Text>
        )}
        {extra}
      </div>
    );
  }

  // --- Variant: Card ---
  if (variant === "card") {
    return (
      <Card
        className={className}
        style={{
          background: color || "var(--color-sky-1)",
          borderRadius: "var(--border-radius-2)",
          textAlign: "center",
          padding: density === "compact" ? 8 : 16,
          ...style,
        }}
        size="small"
        bordered={false}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            margin: density === "compact" ? "4px 0" : "8px 0",
            justifyContent: "center",
            width: "100%",
            minWidth: "0",
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              flex: 1,
              width: "100%",
              minWidth: "0",
            }}
          >
            {label && (
              <div style={{ minWidth: 0, width: "100%" }}>
                <CustomTooltip content={formatTitle(label)}>
                  <Typography.Text
                    className="vzbl-text-2 vzbl-clamp-1-line"
                    style={{ maxWidth: "100%" }}
                  >
                    {formatTitle(label)}
                  </Typography.Text>
                </CustomTooltip>
              </div>
            )}
            <CustomTooltip content={formattedValue}>
              <Typography.Text
                className={`${getHeaderClass(
                  formattedValue
                )} vzbl-clamp-1-line`}
                style={{ textAlign: "center" }}
              >
                {formattedValue}
              </Typography.Text>
            </CustomTooltip>
            {unit && (
              <Typography.Text
                className="vzbl-text-2"
                style={{ textAlign: "center", display: "block" }}
              >
                {unit}
              </Typography.Text>
            )}
            {isValidPercent(percent) && (
              <span
                className="vzbl-text-2 vzbl-pill-sm vzbl-pill-clear"
                style={{
                  marginTop: 4,
                  fontSize: density === "compact" ? 10 : 12,
                  padding: density === "compact" ? "1px 6px" : "2px 8px",
                  display: "inline-block",
                }}
              >
                {percent}
                <span style={{ marginLeft: 4, display: "inline-flex" }}>
                  {getChangeIcon(changeType)}
                </span>
              </span>
            )}
          </div>
        </div>
        {extra}
      </Card>
    );
  }

  // --- Variant: Modern ---
  if (variant === "modern") {
    return (
      <Card
        className={`${className} flex-col-full`}
        style={{
          border: "solid 1px var(--color-foreground-quaternary)",
          borderRadius: "var(--border-radius-2)",
          textAlign: "center",
          padding: density === "compact" ? 8 : 16,
          ...style,
        }}
        size="small"
      >
        {label && (
          <div style={{ minWidth: 0, width: "100%" }}>
            <CustomTooltip content={formatTitle(label || "")}>
              <Typography.Text
                className="vzbl-text-2 vzbl-clamp-1-line"
                style={{
                  marginBottom: density === "compact" ? 4 : 8,
                  maxWidth: "100%",
                }}
              >
                {formatTitle(label)}
              </Typography.Text>
            </CustomTooltip>
          </div>
        )}
        <CustomTooltip content={formattedValue}>
          <Typography.Text
            className={getHeaderClass(formattedValue)}
            style={{ display: "block" }}
          >
            {formattedValue}
          </Typography.Text>
        </CustomTooltip>
        {unit && (
          <Typography.Text
            className="vzbl-text-2"
            style={{ textAlign: "center", display: "block" }}
          >
            {unit}
          </Typography.Text>
        )}
        {isValidPercent(percent) && (
          <Typography.Text className="vzbl-text-3">{percent}</Typography.Text>
        )}
        {extra}
      </Card>
    );
  }

  // --- Variant: Minimal ---
  if (variant === "minimal") {
    return (
      <span className={className} style={style}>
        <CustomTooltip content={formattedValue}>
          <Typography.Text className={`${getHeaderClass(formattedValue)}`}>
            {formattedValue}
          </Typography.Text>
        </CustomTooltip>
        {unit && (
          <Typography.Text className="vzbl-text-2 vzbl-clamp-1-line">
            {" "}
            {unit}
          </Typography.Text>
        )}
        {extra}
      </span>
    );
  }

  // --- Variant: Rich ---
  if (variant === "low-profile") {
    return (
      <Space
        direction="vertical"
        size={density === "compact" ? 2 : "small"}
        className={`${className} vzbl-card-metric`}
        style={{ width: "100%", ...style }}
      >
        {label && (
          <div style={{ minWidth: 0, width: "100%" }}>
            <CustomTooltip content={formatTitle(label || "")}>
              <Typography.Text
                className="vzbl-text-2 vzbl-clamp-1-line"
                style={{ maxWidth: "100%" }}
              >
                {formatTitle(label)}
              </Typography.Text>
            </CustomTooltip>
          </div>
        )}
        <Space direction="vertical" size={2} style={{ width: "100%" }}>
          <Space>
            <CustomTooltip content={formattedValue}>
              <Typography.Text
                className={`${getHeaderClass(
                  formattedValue
                )} vzbl-clamp-1-line`}
              >
                {formattedValue}
              </Typography.Text>
            </CustomTooltip>
            {(isValidPercent(change) || changeType) && (
              <Space size={4}>
                {getChangeIcon(changeType)}
                {change !== "0%" && (
                  <Typography.Text className="vzbl-text-3">
                    {typeof change === "number" && change > 0 ? "+" : ""}
                    {change}
                  </Typography.Text>
                )}
              </Space>
            )}
          </Space>
          {unit && (
            <Typography.Text className="vzbl-text-2 vzbl-clamp-1-line">
              {unit}
            </Typography.Text>
          )}
        </Space>
      </Space>
    );
  }

  // --- Variant: Default (inline) ---
  return (
    <Space
      direction="horizontal"
      size={density === "compact" ? 2 : "small"}
      className={className}
      style={{ width: "100%", minWidth: "0", ...style }}
    >
      {(typeof showDivider === "boolean" ? showDivider : true) && (
        <Divider
          type="vertical"
          style={{ height: "100%", alignSelf: "stretch" }}
        />
      )}
      <Space
        direction="vertical"
        size={density === "compact" ? 2 : "small"}
        className={className}
        style={{ width: "100%", minWidth: "0" }}
      >
        {label && (
          <div style={{ minWidth: 0, width: "100%" }}>
            <span
              className="vzbl-text-2 vzbl-clamp-1-line"
              style={{ maxWidth: "100%" }}
            >
              {formatTitle(label)}
            </span>
          </div>
        )}
        <Space direction="vertical" size={2} style={{ width: "100%" }}>
          <Space direction="vertical" size={0}>
            <CustomTooltip content={formattedValue}>
              <Typography.Text
                className={`${getHeaderClass(
                  formattedValue
                )} vzbl-clamp-1-line`}
              >
                {formattedValue}
              </Typography.Text>
            </CustomTooltip>
            {unit && (
              <Typography.Text
                className="vzbl-text-2"
                style={{ textAlign: "center", display: "block" }}
              >
                {unit}
              </Typography.Text>
            )}
          </Space>
          {(isValidPercent(change) || changeType) && (
            <Space size={4}>
              {getChangeIcon(changeType)}
              {change !== undefined && (
                <Typography.Text className="vzbl-text-3">
                  {typeof change === "number" && change > 0 ? "+" : ""}
                  {change}%
                </Typography.Text>
              )}
            </Space>
          )}
          {extra}
        </Space>
      </Space>
    </Space>
  );
}
